import { MesLogo } from '@/components/brand/mes-logo'
import { EnvBanner } from '@/components/layout/env-banner'
import { LoginForm } from '@/features/auth/components/login-form'
import { currentUserQuery } from '@/features/auth/queries/current-user-query'
import type { AuthStatus } from '@/features/auth/types'
import { mesEnv } from '@/lib/env'
import { createFileRoute, redirect } from '@tanstack/react-router'
import * as z from 'zod'

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: async ({ context }) => {
    const status = await (context.queryClient
      .ensureQueryData(currentUserQuery())
      .catch(() => null) as Promise<AuthStatus | null>)
    if (status?.authenticated) {
      throw redirect({ to: '/projects/' })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  // MES Login screen — dark surface, lime ambient shadow, brand logo,
  // eyebrow tagline, mono footer. Reference:
  // MES Design System-handoff/ui_kits/mes-app/screens/Login.jsx.
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-bg-primary p-6">
      <EnvBanner className="absolute inset-x-0 top-0" />
      {/* Width pinned to 446 px to mirror the handoff card. The handoff
          uses inline `width: 380` + content-box, so padding + border land
          outside that 380 px (total 446 px). Tailwind's preflight pins
          border-box, so we have to specify the *total* width directly. */}
      <div className="w-full max-w-[446px] rounded-[8.4px] border border-border bg-card p-8 shadow-floating">
        <div className="mb-6 flex justify-center text-accent">
          <MesLogo variant="full" size={28} />
        </div>
        {/* Handoff h1 inherits body's --leading-base (1.5). Our @layer base
            tightens h1 to --leading-tight (1.15), which pulls the eyebrow
            up. Override locally — other h1s across the app use the dense
            default and shouldn't loosen. */}
        <h1 className="text-center text-xl font-extrabold tracking-tight leading-[1.5]">Вхід</h1>
        <p className="mt-1 mb-7 text-center text-2xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Виробничий облік цеху
        </p>
        <LoginForm />
        <div className="mt-6 border-t border-border pt-4 text-center font-mono text-2xs tracking-[0.04em] text-muted-foreground">
          MES · {mesEnv}
        </div>
      </div>
    </div>
  )
}
